import {
  addTableRecommendations,
  bulkUpdateRecommendations,
  getTableRecommendationsStateForThread,
  updateTableRecommendation,
} from '@/store/reducers/TableRecommendationsStore';
import { addTableRows } from '@/store/thunks/UpdateTable';
import { EventType } from '@/models/Interaction';
import { getChatAPI } from '@/api/ChatApi';
import { getCurrentThread } from '@/store/reducers/UIState';
import { getTableAPI } from '@/api/TableApi';
import { getTableStateForThread } from '@/store/reducers/TableWidgetStore';
import { LoadingTable } from '@/components/table/LoadingTable';
import {
  mkRelatedAddSelectedBtnTrackAttrs,
  mkRelatedCloseBtnTrackAttrs,
  mkRelatedOpenBtnTrackAttrs,
} from '@/analytics/props/litTable';
import { optAppConfig } from '@/utils/config';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { populateTableCellsData } from '@/store/thunks/PopulateTableCellsData';
import { TABLE_ANNOTATION } from 'configs/NoraVariants';
import { TablePaperRecommendationResponse } from '@/@types/Table';
import { TableRow, tableRowFactory } from '@/models/Table';
import { TableToolPaper } from '@/actions/ProcessUIToolCalls';
import { TableView, TableViewMode } from '@/components/table/TableView';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import {
  Box,
  Button,
  CardHeader,
  IconButton,
  styled,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

interface TableControlsProps {}

export const TableControls: React.FunctionComponent<
  TableControlsProps
> = (): React.ReactNode => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatApi = getChatAPI();

  const tableApi = getTableAPI();
  const dispatch = useAppDispatch();
  const currentThread = useAppSelector(getCurrentThread);

  const tableRecommendationsData = useAppSelector((state) =>
    getTableRecommendationsStateForThread(state, currentThread?.id),
  );
  const tableData = useAppSelector((state) =>
    getTableStateForThread(state, currentThread?.id),
  );
  const corpusIds =
    tableData?.rows.reduce((corpusIds: number[], row: TableRow) => {
      if (row.paperCorpusId) {
        corpusIds.push(row.paperCorpusId);
      }
      return corpusIds;
    }, []) ?? [];
  const recommendations = tableRecommendationsData?.rows ?? [];
  const recommendationsColumns = tableRecommendationsData?.columns ?? [];
  const recommendationsCells = tableRecommendationsData?.cells ?? {};
  const numRowsSelected = recommendations.filter(
    (row) => row.isSelected,
  ).length;

  useEffect(() => {
    if (recommendations.length !== 0 && corpusIds.length !== 0) {
      chatApi.trackEvent({
        type: EventType.WIDGET_TABLE_RELATED_PAPERS,
        threadId: currentThread?.id,
        data: {
          table_id: currentThread?.id,
          table_corpus_ids: corpusIds,
          rowz_data: {
            rows: recommendations,
            columns: recommendationsColumns,
            cells: recommendationsCells,
          },
        },
      });
    }
  }, [tableRecommendationsData, corpusIds]);

  const openDrawer = useCallback(() => {
    if (recommendations.length === 0) {
      setIsLoading(true);
      setError(null);
      tableApi
        .getTablePaperRecommendations(corpusIds)
        .then((response: { data: TablePaperRecommendationResponse }) => {
          if (currentThread) {
            const recommendations = response.data.papers.map(
              (paper: { corpus_id: number; title: string }) => ({
                paperCorpusId: paper.corpus_id,
                displayValue: paper.title,
              }),
            );
            const recommendationsRows = recommendations.map(tableRowFactory);

            dispatch(
              addTableRecommendations({
                currentThreadId: currentThread.id,
                recommendations: recommendationsRows,
              }),
            );
            dispatch(
              populateTableCellsData({
                threadId: currentThread?.id,
                rows: recommendationsRows,
              }),
            );
          }
          setIsLoading(false);
          setIsExpanded(true);
        })
        .catch((error: any) => {
          console.log('Error fetching recommended papers', error);
          setIsLoading(false);
          setError(
            'Error fetching recommended papers. Please try again later.',
          );
        });
    }
    setIsExpanded(true);
  }, [recommendations, corpusIds]);

  const handleChangeRowSelection = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rowId = event.target.value;
      const row = recommendations.find((r) => r.id === rowId);

      if (!currentThread || !row) {
        console.log('No current thread or row found');
        return;
      }

      const updatedRow: TableRow = {
        ...row,
        isSelected: event.target.checked,
      };

      dispatch(
        updateTableRecommendation({
          currentThreadId: currentThread.id,
          row: updatedRow,
        }),
      );
    },
    [dispatch, currentThread, recommendations],
  );

  const handleSelectAllRows = useCallback(() => {
    if (!currentThread) {
      return;
    }

    // if everything is selected, deselect the recommendations
    if (numRowsSelected === recommendations.length) {
      dispatch(
        bulkUpdateRecommendations({
          currentThreadId: currentThread?.id,
          updates: { isSelected: false },
        }),
      );
      return;
    }

    // if nothing or indeterminate, select all rows
    dispatch(
      bulkUpdateRecommendations({
        currentThreadId: currentThread?.id,
        updates: { isSelected: true },
      }),
    );
  }, [dispatch, recommendations, currentThread, numRowsSelected]);

  const addSelectedToTable = useCallback(() => {
    if (!currentThread) {
      return;
    }
    const currentThreadId = currentThread.id;
    const papers: TableToolPaper[] = recommendations
      .filter((row) => row.isSelected && row.paperCorpusId)
      .map((row) => ({
        corpusId: row.paperCorpusId || -1, // ugly hack to make the type work
        title: row.displayValue,
      }));
    dispatch(addTableRows({ currentThreadId, papers }));
  }, [dispatch, recommendations, currentThread, numRowsSelected]);

  if (!isExpanded && noraExperienceVariant !== TABLE_ANNOTATION) {
    return (
      <CollapsedContainer>
        <Button
          sx={{
            color: 'rgba(250, 242, 243, 0.8)', // cream-80
            borderColor: 'currentColor',
          }}
          variant="outlined"
          onClick={openDrawer}
          {...mkRelatedOpenBtnTrackAttrs()}
        >
          See related papers
        </Button>
      </CollapsedContainer>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <CloseButton
        onClick={() => setIsExpanded(false)}
        size="small"
        {...mkRelatedCloseBtnTrackAttrs()}
      >
        <HighlightOffIcon />
      </CloseButton>
      <CardHeader
        title={
          <>
            Papers related to the table
            {numRowsSelected > 0 && (
              <Button
                onClick={addSelectedToTable}
                {...mkRelatedAddSelectedBtnTrackAttrs({
                  selectedRowCount: numRowsSelected,
                })}
              >
                Add selected to the table
              </Button>
            )}
          </>
        }
      />
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: error ? 'error.main' : 'black',
          }}
        >
          {isLoading && <LoadingTable />}
          {error && `${error}`}
        </Typography>
      </Box>
      <Box></Box>
      <PermissionsContext.Provider value={{ isReadOnly: true }}>
        <Box sx={{ height: '40vh', overflow: 'scroll' }}>
          {recommendations.length > 0 && (
            <TableView
              rows={recommendations}
              columns={recommendationsColumns}
              cells={recommendationsCells}
              numRowSelected={numRowsSelected}
              handleSelectAllRows={handleSelectAllRows}
              handleChangeRowSelection={handleChangeRowSelection}
              mode={TableViewMode.RELATED_PAPERS}
            />
          )}
        </Box>
      </PermissionsContext.Provider>
    </Box>
  );
};

const CollapsedContainer = styled(Box)`
  width: 100%;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.color['extra-dark-teal-20'].hex};
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1)};
`;
